import React, { Fragment, FunctionComponent, useState, useMemo, useEffect } from 'react';
import {
    List,
    ListItem,
    ListItemText,
    FormControlLabel,
    Radio,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import styles from "./ReferralService.Styles";
import VendorEntity from '../../../../entities/Vendor.Entity';
import { CustomMessageBox } from '../../../generic-components/custom-message-box/CustomMessageBox.Component';

interface IMedicalServiceListProps {
    vendors: Array<VendorEntity>;
}

export const ReferralVendorList: FunctionComponent<IMedicalServiceListProps> = ({ vendors }: IMedicalServiceListProps) => {
    const classes = styles();
    let messageToDisplay = '';

    if (vendors.length > 0 && vendors[0].vendorId === 0) {
        messageToDisplay = vendors[0].vendorName;
    }

    const preferredVendors = useMemo(() => vendors.filter(x => x.isPreferred), [vendors]);
    const partnerVendors = useMemo(() => vendors.filter(x => !x.isPreferred), [vendors]);

    // Expand 'preferred' if it has items, otherwise expand 'partner'
    const [expanded, setExpanded] = useState<string | false>(
        preferredVendors.length > 0 ? 'preferred' : 'partner'
    );

    const handleChange = (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    useEffect(() => {
        if (preferredVendors.length > 0) {
            setExpanded('preferred');
        } else if (partnerVendors.length > 0) {
            setExpanded('partner');
        } else {
            setExpanded(false);
        }
    }, [preferredVendors, partnerVendors]);

    return (
        <Fragment>
            {
                vendors.length > 0 && vendors[0].vendorId > 0 ? (
                    <Fragment>
                        {/* Client Preferred Vendors */}
                        {preferredVendors.length > 0 && (
                            <Accordion
                                expanded={expanded === 'preferred'}
                                onChange={handleChange('preferred')}
                                className={classes.accordionRoot}
                            >
                               <AccordionSummary
                                    aria-controls="preferred-content"
                                    id="preferred-header"
                                    classes={{ 
                                        content: classes.accordionSummaryContent,
                                    }}
                                >
                                    <span className={classes.expandIconLeft}>
                                        <ExpandMoreIcon 
                                            className={expanded === 'preferred' ? classes.iconExpanded : classes.iconCollapsed}
                                        />
                                    </span>
                                    <Typography variant="caption" className={classes.headerTitleBold}>
                                        Client Preferred Vendors
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails className={classes.accordionDetails}>
                                    <List component="div" disablePadding>
                                        {preferredVendors.map((vendor) => (
                                            <ListItem key={vendor.vendorId} button className={classes.nested}>
                                                <ListItemText>
                                                    <FormControlLabel
                                                        value={vendor.vendorId}
                                                        control={<Radio className={classes.radioButton} />}
                                                        label={
                                                            <span className={classes.radioButtonLabel}>
                                                                {vendor.vendorName}
                                                            </span>
                                                        }
                                                    />
                                                </ListItemText>
                                            </ListItem>
                                        ))}
                                    </List>
                                </AccordionDetails>
                            </Accordion>
                        )}

                        {/* CCMSI Vendor Partner */}
                        {partnerVendors.length > 0 && (
                            <Accordion
                                expanded={expanded === 'partner'}
                                onChange={handleChange('partner')}
                                className={classes.accordionRoot}
                            >
                                <AccordionSummary
                                    aria-controls="partner-content"
                                    id="partner-header"
                                    classes={{ 
                                        content: classes.accordionSummaryContent,
                                    }}
                                >
                                    <span className={classes.expandIconLeft}>
                                        <ExpandMoreIcon 
                                            className={expanded === 'partner' ? classes.iconExpanded : classes.iconCollapsed}
                                        />
                                    </span>
                                    <Typography variant="caption" className={classes.listHeaderSubTitle}>
                                        Other
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails className={classes.accordionDetails}>
                                    <List component="div" disablePadding>
                                        {partnerVendors.map((vendor) => (
                                            <ListItem key={vendor.vendorId} button className={classes.nested}>
                                                <ListItemText>
                                                    <FormControlLabel
                                                        value={vendor.vendorId}
                                                        control={<Radio className={classes.radioButton} />}
                                                        label={
                                                            <span className={classes.radioButtonLabel}>
                                                                {vendor.vendorName}
                                                            </span>
                                                        }
                                                    />
                                                </ListItemText>
                                            </ListItem>
                                        ))}
                                    </List>
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </Fragment>
                ) : (
                    <div className={classes.customMessage}>
                        <CustomMessageBox label={messageToDisplay} />
                    </div>
                )
            }
        </Fragment>
    );
};